import type { Theme } from "@mui/material/styles";

/**
 * What the published marketing pages measure. Every value is read off the live
 * site with Playwright at the viewport it applies to, so nothing here is a
 * design decision and colour names follow the site's own CSS custom properties.
 */

/**
 * The published site's own breakpoints, and all of them: its stylesheet steps
 * at 991, 767 and 479 and nowhere else.
 */
export const SITE_STEPS = { wide: 992, card: 768, tiny: 480 };

/**
 * The same three steps under MUI's own names, for the marketing theme. With
 * them registered, `theme.breakpoints.down("md")` and the `{ md: … }` shorthand
 * step where the site does rather than where MUI's defaults do. There is no
 * step above `wide`, so `xl` is `wide` again and nothing can be said about a
 * larger screen by accident. `__tests__/responsiveness.test.ts` holds this and
 * the queries below to each other.
 */
export const MARKETING_BREAKPOINTS = {
  xs: 0,
  sm: SITE_STEPS.tiny,
  md: SITE_STEPS.card,
  lg: SITE_STEPS.wide,
  xl: SITE_STEPS.wide,
};

/**
 * For the handful of places with no theme in scope. Not interchangeable with
 * `theme.breakpoints.down(n)`, which stops at `n - 0.05px`.
 */
export const WIDE = `@media (min-width: ${SITE_STEPS.wide}px)`;
export const NOT_WIDE = `@media (max-width: ${SITE_STEPS.wide - 1}px)`;
export const MID = `@media (max-width: ${SITE_STEPS.card - 1}px)`;
export const TINY = `@media (max-width: ${SITE_STEPS.tiny - 1}px)`;

/**
 * The content column each surface is built on. Four separate measurements, not
 * one value drifting: the published pages really do frame them differently.
 */
export const FRAME = {
  header: 1360,
  academy: 1440,
  blog: 1328,
  footer: 1272,
};

/** `.faq-main_cms_wrap`: the rail every FAQ band draws its rows in. */
export const FAQ_RAIL = "1020.8px";

/** The academy's gutter ladder, taken by both academy surfaces alike. */
const ACADEMY_GUTTER = "36px";
const ACADEMY_GUTTER_SNUG = "30.08px";
const ACADEMY_GUTTER_TIGHT = "16px";

export const marketingPalette = {
  font: '"Rubik", sans-serif',
  /** `--colors--primary-3`, body copy and headings. */
  ink: "#041E39",
  /** `--colors--gray-1`, card titles. */
  inkMuted: "#353E44",
  /** `--colors--gray-2`, summaries and secondary lines. */
  inkSubtle: "#4F5C65",
  /** Bylines, the quietest text on the page. */
  inkFaint: "#697279",
  /** The 1px rule around every card. */
  hairline: "rgba(4, 30, 57, 0.1)",
  /** `--colors--gray-3`, arrow badges, figure borders, the contents rail rule. */
  wash: "#E9EEF1",
  /** `--colors--gray-4`, the FAQ band, author card, search field, row hovers. */
  surface: "#F7FAFC",
  /** `--colors--gray-5`, quiet ink on the dark band, and the hover pill's arrow. */
  gray5: "#E6E6E6",
  /** `--colors--dark-bg`, the footer band and the call-to-action card. */
  darkGround: "#071727",
  /** The accent's hover step; no palette role carries it. */
  accentHover: "#00CCA3",
  /** `--colors--outline`, the rule around the home page's product cards. */
  outline: "rgba(79, 92, 101, 0.21)",
  /** `--colors--danger`, the label over a problem and a crossed-out cell. */
  danger: "#DA2424",
  /** The resting rule on every pill control, holding the pill's height steady against the selected state's own 1px rule. */
  pillRule: "#EEF3F5",
  /** The fill those pills take under the pointer, where they take one. */
  pillHover: "#DFE5E7",
  /** `--colors--purple`, the home page's one non-accent button fill. */
  purple: "#6471E3",
  /** That fill's hover step, from `.button_new.is-purple-c:hover`. */
  purpleHover: "#7885FC",

  /* The home page's tinted panels, each the measured fill of the block it names. */
  /** The hero card. */
  heroPanel: "#E7F5E8",
  /** The ground under a hero's checks, on the seven heroes that set them on one. */
  checksPanel: "#F3F6F8",
  /** The plain feature card: a lilac wash inside a near-white rule. */
  plainCard: "rgba(242, 244, 255, 0.65)",
  plainCardRule: "#F5F9FB",
  /** The two flat grounds the published split sections take beside the surface. */
  sectionMint: "#F3FBF0",
  sectionLilac: "#F6F6FD",
  /** `.main_header_box.is-purple-blue`: the third product row's ground. */
  sectionPeriwinkle: "#EEF2FE",
  /** The Trustpilot band. */
  trustPanel: "#E0F7E4",
  /** The Proxy Servers premium group, and its IP-count chip. */
  premiumPanel: "#E6F4EA",
  /** The Static Residential group, which carries the same shape in blue. */
  premiumPanelBlue: "#E6F1F7",
  /** The support and developer cards. */
  featureCard: "#F6F7FF",
  /** The "Supported by Trusted Team in Silicon Valley" chip. */
  labelChip: "#EBF0EE",
  /** The accent at 8%, under the "Looking for a custom solution?" banner. */
  bannerTint: "rgba(0, 142, 114, 0.08)",
  /** The stat band's ground and, at full strength, its numerals. */
  statInk: "#074227",
  /** The one highlighted span, on the dark call-to-action bar. */
  mint: "#54EACC",
  /** `.section_new.is-secure-about`: the ground of the about page's dark band. */
  secureBand: "#091625",
  /** `.compliance-bullet_item.is-large`: the discount green at 16%, under a ticked row. */
  complianceChip: "rgba(60, 186, 88, 0.16)",
  /** `.button_secondary-primary:hover`: the accent at 10%, the outlined button's hover fill. */
  accentWash: "rgba(26, 179, 148, 0.1)",

  /* The pricing page's fills, the only ones no other surface uses. */
  /** The "Save 20%" chip: this ink on the tint below it. */
  discountInk: "#3CBA58",
  /** The red the feature price tables print a discount in, and the sales form its failure line. */
  alertInk: "#DA2424",
  /** The fill a product card takes under the pointer. */
  cardHoverTint: "#E6F7F3",
  /** The fill the home page's product tabs take under the pointer. */
  tabHoverTint: "#F3F6F8",
  discountTint: "rgba(95, 255, 150, 0.11)",
  /** The four-metric strip under each product's cards. */
  metricTint: "rgba(60, 186, 88, 0.08)",
  /** The "save 30%" pill over the Yearly control, the page's one non-green chip. */
  savePanel: "#DCE0F8",
  saveInk: "#414EC5",

  /* The price table's buy control: a third green, one step off both the accent and its deep step, so neither can stand in for it. */
  tableButtonInk: "#008E72",
  tableButtonTint: "#E8F7F4",
  tableButtonRule: "rgba(26, 179, 148, 0.58)",

  /* The two panels the product pages close their feature section on. Each carries its own chip fill and ink, and no hue here occurs elsewhere on the site. */
  featureAzure: "#F1F6FF",
  featureAzureChip: "rgba(100, 113, 227, 0.1)",
  featureAzureInk: "#5362E7",
  featureLilac: "#F1E6FC",
  featureLilacChip: "rgba(193, 152, 238, 0.12)",
  featureLilacInk: "#662CE0",
  featurePeach: "#FFF2EE",
  featurePeachChip: "rgba(226, 147, 118, 0.12)",
  featurePeachRule: "#FCE6E0",
  /** The peach metric card under the pointer: its tint deepens and its rule turns orange. */
  featurePeachChipHover: "rgba(226, 147, 118, 0.2)",
  featurePeachRuleHover: "#DF674C",
  featurePeachInk: "#B43A1E",

  /** The compare band's "Currently Viewing" marker. */
  compareMarker: "rgba(7, 23, 39, 0.06)",
};

/** `--colors--primary-1`: chips, pagination, the featured card's hover rule. */
export const ACCENT = "#1AB394";

/** The accent's dark step: an open disclosure, and every hovered bar control. */
export const ACCENT_DEEP = "#008266";

/** The accent's palest step: the tint under a hovered or open bar control. */
export const ACCENT_TINT = "#EDFCF9";

/** The accent button sits on a glow of its own colour rather than a neutral elevation. */
export const BUTTON_GLOW = "rgba(27, 179, 148, 0.6) 0px 6px 12px -4px";

/** The same glow under the pointer: opaque, and thrown a little further. */
export const BUTTON_GLOW_HOVER = "rgb(27, 179, 148) 0px 8px 12px -4px";

/** The academy's gutter at each step, which its bar and its frame both keep. */
export const ACADEMY_GUTTER_SX = (theme: Theme) => ({
  px: ACADEMY_GUTTER_TIGHT,
  [theme.breakpoints.up(SITE_STEPS.tiny)]: { px: ACADEMY_GUTTER_SNUG },
  [theme.breakpoints.up(SITE_STEPS.card)]: { px: ACADEMY_GUTTER },
});

/** The academy's frame: the hub and the article on one measure, with the gutter their step allows. */
export const ACADEMY_FRAME_SX = (theme: Theme) => ({
  maxWidth: FRAME.academy,
  pb: 6,
  ...ACADEMY_GUTTER_SX(theme),
});
